import Phaser from 'phaser';
import {
  COLORS,
  FASE_DAGER,
  FASE_KOSTNAD,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  STATUS_PILLE_FARGE,
  TIER_COLORS,
  fontStr,
} from '@/config';
import { gameState } from '@/state/store';
import type { Anmeldelse, Author, ContractedBook, ContractStatus } from '@/data/types';
import { SVEN_SPILLER_AUTHOR_ID, spillerFulltNavn } from '@/data/types';
import { KRITIKERE } from '@/data/kritikere';
import { stjerneStreng } from '@/systems/anmeldelser';
import { animateModalGlide } from '@/ui/modalGlide';

const SHEET_WIDTH = 1000;
const ROW_WIDTH = 940;
const ROW_PADDING = 24;
const ROW_GAP = 18;

type RowMeta = {
  pille?: Phaser.GameObjects.Rectangle;
  tween?: Phaser.Tweens.Tween;
};

export class ProductionModal {
  private layer?: Phaser.GameObjects.Container;
  private unsubscribe?: () => void;
  private pulseTweens: Phaser.Tweens.Tween[] = [];
  private utvidedeAnmeldelser = new Set<string>();
  private scrollY = 0;
  private scrollMax = 0;
  private førsteRedigeringRect: { x: number; y: number; w: number; h: number } | null = null;

  constructor(private scene: Phaser.Scene) {}

  førsteRedigeringKnappRect(): { x: number; y: number; w: number; h: number } | null {
    if (!this.layer) return null;
    return this.førsteRedigeringRect;
  }

  open(): void {
    if (this.layer) return;
    this.layer = this.scene.add.container(0, 0).setDepth(120);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.55)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    this.scene.input.on('wheel', this.onWheel, this);
    this.render();
    animateModalGlide(this.scene, this.layer);
    this.unsubscribe = gameState.subscribe(() => this.render());
  }

  close(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
    this.scene.input.off('wheel', this.onWheel, this);
    this.stopPulses();
    this.utvidedeAnmeldelser.clear();
    this.scrollY = 0;
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    if (!this.layer) return;
    this.stopPulses();
    while (this.layer.list.length > 1) {
      const last = this.layer.list[this.layer.list.length - 1];
      this.layer.remove(last, true);
    }

    const snap = gameState.snapshot;
    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = 96;
    const sheetH = GAME_HEIGHT - 192;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, sheetH, COLORS.beige, 0.98)
      .setOrigin(0, 0)
      .setStrokeStyle(3, COLORS.wine, 0.6);
    this.layer.add(sheet);

    const headerY = 132;
    const header = this.scene.add
      .text(
        sheetX + ROW_PADDING,
        headerY,
        `Bøker (${snap.iProduksjon.length + snap.utgitteBoker.length})`,
        {
          fontFamily: FONTS.display,
          fontSize: fontStr(46),
          color: '#862736',
          wordWrap: { width: SHEET_WIDTH - ROW_PADDING * 2 - 110 },
        },
      )
      .setOrigin(0, 0);
    this.layer.add(header);

    const subtitle = this.scene.add
      .text(sheetX + ROW_PADDING, headerY + 84, `Dag ${snap.dag} · ${snap.kroner.toLocaleString('nb-NO')} kr`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(22),
        color: '#5a4a3a',
      })
      .setOrigin(0, 0);
    this.layer.add(subtitle);

    const closeBtn = this.scene.add
      .text(sheetX + SHEET_WIDTH - ROW_PADDING, headerY - 4, '×', {
        fontFamily: FONTS.display,
        fontSize: fontStr(72),
        color: '#1a1a1a',
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });
    closeBtn.on('pointerup', () => this.close());
    this.layer.add(closeBtn);

    const listX = sheetX + (SHEET_WIDTH - ROW_WIDTH) / 2;
    const listTop = headerY + 160;
    const listBunn = sheetY + sheetH - 32;
    const listHoyde = listBunn - listTop;

    const totalBoker = snap.iProduksjon.length + snap.utgitteBoker.length;
    if (totalBoker === 0) {
      const tomBoks = this.scene.add
        .rectangle(listX, listTop, ROW_WIDTH, 220, COLORS.white, 0.9)
        .setOrigin(0, 0)
        .setStrokeStyle(2, COLORS.oakDark, 0.4);
      const tomTxt = this.scene.add
        .text(
          listX + ROW_WIDTH / 2,
          listTop + 110,
          'Ingen bøker i produksjon ennå.\nTilby en kontrakt fra innboksen.',
          {
            fontFamily: FONTS.body,
            fontSize: fontStr(26),
            color: '#1a1a1a',
            align: 'center',
          },
        )
        .setOrigin(0.5);
      this.layer.add([tomBoks, tomTxt]);
      return;
    }

    const listContainer = this.scene.add.container(0, 0);
    this.layer.add(listContainer);

    const maske = this.scene.make.graphics({ x: 0, y: 0 }, false);
    maske.fillStyle(0xffffff);
    maske.fillRect(listX - 4, listTop, ROW_WIDTH + 8, listHoyde);
    const maskeObj = maske.createGeometryMask();
    listContainer.setMask(maskeObj);

    let y = listTop - this.scrollY;
    let bygdHoyde = 0;

    this.førsteRedigeringRect = null;
    const alleBoker = [...snap.iProduksjon, ...snap.utgitteBoker];
    for (const bok of alleBoker) {
      const author = bok.authorId ? snap.authors[bok.authorId] : undefined;
      const rad = this.buildRad(listX, y, bok, author, snap.kroner, snap.dag);
      listContainer.add(rad.container);
      if (!this.førsteRedigeringRect && bok.status === 'kontrahert') {
        const actionY = y + 320 - 96;
        const actionW = ROW_WIDTH - ROW_PADDING * 2;
        this.førsteRedigeringRect = {
          x: listX + ROW_PADDING,
          y: actionY,
          w: actionW,
          h: 66,
        };
      }
      y += rad.height + ROW_GAP;
      bygdHoyde += rad.height + ROW_GAP;
    }

    this.scrollMax = Math.max(0, bygdHoyde - listHoyde);
    if (this.scrollY > this.scrollMax) {
      this.scrollY = this.scrollMax;
    }

    this.bindScroll(listX, listTop, listHoyde);
  }

  private bindScroll(listX: number, listTop: number, listHoyde: number): void {
    if (!this.layer) return;
    if (this.scrollMax <= 0) return;
    const scrollSone = this.scene.add
      .rectangle(listX, listTop, ROW_WIDTH, listHoyde, 0x000000, 0)
      .setOrigin(0, 0)
      .setInteractive();
    let dragStart = 0;
    let scrollStart = 0;
    scrollSone.on('pointerdown', (p: Phaser.Input.Pointer) => {
      dragStart = p.y;
      scrollStart = this.scrollY;
    });
    scrollSone.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      const delta = dragStart - p.y;
      this.scrollY = Math.max(0, Math.min(this.scrollMax, scrollStart + delta));
      this.render();
    });
    this.layer.add(scrollSone);
  }

  private onWheel = (
    _pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    dy: number,
  ): void => {
    if (!this.layer || this.scrollMax <= 0) return;
    this.scrollY = Math.max(0, Math.min(this.scrollMax, this.scrollY + dy));
    this.render();
  };

  private buildRad(
    x: number,
    y: number,
    bok: ContractedBook,
    author: Author | undefined,
    kroner: number,
    dag: number,
  ): { container: Phaser.GameObjects.Container; height: number } {
    const meta: RowMeta = {};
    const container = this.scene.add.container(x, y);

    const erUtgitt = bok.status === 'utgitt';
    const utvidet = this.utvidedeAnmeldelser.has(bok.isbn);
    const grunnHoyde = 320;
    const utgittTillegg = erUtgitt ? 140 : 0;
    const sitatTillegg = erUtgitt && utvidet ? this.beregnSitatHoyde(bok) : 0;
    const høyde = grunnHoyde + utgittTillegg + sitatTillegg;

    const bg = this.scene.add
      .rectangle(0, 0, ROW_WIDTH, høyde, COLORS.white, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.oakDark, 0.4);
    container.add(bg);

    const oversettelse = gameState.internasjonaleAvtaler.find((a) => a.isbn === bok.isbn);
    if (oversettelse) {
      const goldBorder = this.scene.add
        .rectangle(0, 0, 6, høyde, 0xb8860b, 1)
        .setOrigin(0, 0);
      container.add(goldBorder);
    }

    const erDinBok = bok.authorId === SVEN_SPILLER_AUTHOR_ID;
    if (erDinBok) {
      const gulBorder = this.scene.add
        .rectangle(0, 0, 6, høyde, COLORS.yellowWarm, 1)
        .setOrigin(0, 0);
      container.add(gulBorder);
    }

    const tittel = this.scene.add
      .text(ROW_PADDING, 20, bok.title, {
        fontFamily: FONTS.display,
        fontSize: fontStr(28),
        color: '#1a1a1a',
        wordWrap: { width: ROW_WIDTH - ROW_PADDING * 2 - 180 },
      })
      .setOrigin(0, 0);
    container.add(tittel);

    if (oversettelse) {
      const badgeY = 20;
      const badgeTxt = this.scene.add
        .text(0, 0, `↗ ${oversettelse.sprak}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(14),
          color: '#ffffff',
          fontStyle: '700',
        })
        .setOrigin(0, 0);
      const padX = 10;
      const padY = 4;
      const bw = badgeTxt.width + padX * 2;
      const bh = badgeTxt.height + padY * 2;
      const bx = ROW_WIDTH - ROW_PADDING - 90 - bw - 8;
      const badgeBg = this.scene.add
        .rectangle(bx, badgeY, bw, bh, 0xb8860b, 1)
        .setOrigin(0, 0);
      badgeTxt.setPosition(bx + padX, badgeY + padY);
      container.add(badgeBg);
      container.add(badgeTxt);
    }

    if (erDinBok) {
      const badgeTxt = this.scene.add
        .text(0, 0, '✎ DIN BOK', {
          fontFamily: FONTS.hud,
          fontSize: fontStr(13),
          color: '#862736',
          fontStyle: '700italic',
        })
        .setOrigin(0, 0);
      const padX = 10;
      const padY = 4;
      const bw = badgeTxt.width + padX * 2;
      const bh = badgeTxt.height + padY * 2;
      const bx = ROW_PADDING;
      const by = 108;
      const badgeBg = this.scene.add
        .rectangle(bx, by, bw, bh, COLORS.yellowWarm, 1)
        .setOrigin(0, 0);
      badgeTxt.setPosition(bx + padX, by + padY);
      container.add([badgeBg, badgeTxt]);
    }

    if (erDinBok && !author) {
      const navnTxt = this.scene.add
        .text(ROW_PADDING, 76, spillerFulltNavn(gameState.spillerForfatter.navn), {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#5a4a3a',
        })
        .setOrigin(0, 0);
      container.add(navnTxt);
    } else if (author) {
      const navnTxt = this.scene.add
        .text(ROW_PADDING, 76, author.navn, {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#5a4a3a',
        })
        .setOrigin(0, 0);
      container.add(navnTxt);

      if (author.pseudonym) {
        const pseudoX = ROW_PADDING + navnTxt.width + 8;
        const pseudoTxt = this.scene.add
          .text(pseudoX, 76, '(pseudonym)', {
            fontFamily: FONTS.body,
            fontSize: fontStr(16),
            color: '#6b6b6b',
            fontStyle: 'italic',
          })
          .setOrigin(0, 0);
        container.add(pseudoTxt);
      }
    } else {
      const forfatterTxt = this.scene.add
        .text(ROW_PADDING, 76, bok.author, {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#5a4a3a',
        })
        .setOrigin(0, 0);
      container.add(forfatterTxt);
    }

    const tierBg = this.scene.add
      .rectangle(ROW_WIDTH - ROW_PADDING, 22, 110, 48, TIER_COLORS[bok.tier], 1)
      .setOrigin(1, 0);
    const tierTxt = this.scene.add
      .text(ROW_WIDTH - ROW_PADDING - 55, 30, `Tier ${bok.tier}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(18),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5, 0);
    container.add([tierBg, tierTxt]);

    const pille = this.byggStatusPille(ROW_PADDING, 124, bok, dag, meta);
    container.add(pille);

    if (this.erPaagaaende(bok.status) && bok.faseFerdigDag !== null) {
      const progress = this.byggProgress(ROW_PADDING, 184, bok, dag);
      container.add(progress);
    }

    const actionY = grunnHoyde - 96;
    const action = this.byggAction(ROW_PADDING, actionY, bok, kroner, dag);
    container.add(action);

    if (erUtgitt) {
      const seksjonsY = grunnHoyde;
      const seksjon = this.byggUtgittSeksjon(ROW_PADDING, seksjonsY, bok);
      container.add(seksjon);
    }

    return { container, height: høyde };
  }

  private beregnSitatHoyde(bok: ContractedBook): number {
    return bok.anmeldelser.length * 70;
  }

  private byggStatusPille(
    x: number,
    y: number,
    bok: ContractedBook,
    dag: number,
    meta: RowMeta,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const farge = STATUS_PILLE_FARGE[bok.status];
    const tekst = this.statusTekst(bok, dag);

    const padX = 16;
    const padY = 8;
    const txt = this.scene.add
      .text(padX, padY, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#ffffff',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    const w = txt.width + padX * 2;
    const h = txt.height + padY * 2;

    const bg = this.scene.add
      .rectangle(0, 0, w, h, farge, 1)
      .setOrigin(0, 0);
    bg.setStrokeStyle(0);
    c.add([bg, txt]);
    c.bringToTop(txt);

    meta.pille = bg;

    if (this.erPaagaaende(bok.status)) {
      const tween = this.scene.tweens.add({
        targets: bg,
        alpha: { from: 1, to: 0.7 },
        duration: 750,
        yoyo: true,
        repeat: -1,
      });
      this.pulseTweens.push(tween);
    }

    return c;
  }

  private byggProgress(
    x: number,
    y: number,
    bok: ContractedBook,
    dag: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const w = ROW_WIDTH - ROW_PADDING * 2;
    const h = 8;
    const startDag = bok.faseStartDag;
    const ferdigDag = bok.faseFerdigDag ?? startDag;
    const total = Math.max(1, ferdigDag - startDag);
    const elapsed = Math.max(0, Math.min(total, dag - startDag));
    const frac = elapsed / total;

    const bg = this.scene.add
      .rectangle(0, 0, w, h, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    const fill = this.scene.add
      .rectangle(0, 0, Math.max(2, w * frac), h, COLORS.wine, 1)
      .setOrigin(0, 0);
    c.add([bg, fill]);
    return c;
  }

  private byggAction(
    x: number,
    y: number,
    bok: ContractedBook,
    kroner: number,
    dag: number,
  ): Phaser.GameObjects.Container {
    const w = ROW_WIDTH - ROW_PADDING * 2;
    const h = 66;

    switch (bok.status) {
      case 'kontrahert':
        return this.actionKnapp(x, y, w, h, 'Send i redigering', FASE_KOSTNAD.redigering, kroner, () =>
          gameState.sendIRedigering(bok.isbn),
        );
      case 'redigeres':
        return this.disabledKnapp(x, y, w, h, `Redigeres — ${dagerIgjen(bok, dag)} dager igjen`);
      case 'redigert':
        return this.actionKnapp(x, y, w, h, 'Send til design', FASE_KOSTNAD.design, kroner, () =>
          gameState.sendTilDesign(bok.isbn),
        );
      case 'designes':
        return this.disabledKnapp(x, y, w, h, `Designes — ${dagerIgjen(bok, dag)} dager igjen`);
      case 'designet':
        return this.actionKnapp(x, y, w, h, 'Send til trykk', FASE_KOSTNAD.trykk, kroner, () =>
          gameState.sendTilTrykk(bok.isbn),
        );
      case 'utgis':
        return this.disabledKnapp(x, y, w, h, `Trykkes — ${dagerIgjen(bok, dag)} dager igjen`);
      case 'utgitt':
        return this.utgittPille(x, y, w, h);
    }
  }

  private actionKnapp(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    kostnad: number,
    kroner: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const harRaad = kroner >= kostnad;
    const c = this.scene.add.container(x, y);
    const fill = harRaad ? COLORS.wine : 0xcfc6b7;
    const farge = harRaad ? '#ffffff' : '#7a7060';
    const tekst = harRaad ? `${label} (${kostnad.toLocaleString('nb-NO')} kr)` : 'For lite kapital';

    const bg = this.scene.add.rectangle(0, 0, w, h, fill, 1).setOrigin(0, 0);
    const txt = this.scene.add
      .text(w / 2, h / 2, tekst, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: farge,
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);

    if (harRaad) {
      bg.setInteractive({ useHandCursor: true });
      bg.on('pointerover', () => bg.setAlpha(0.85));
      bg.on('pointerout', () => bg.setAlpha(1));
      bg.on('pointerup', onClick);
    }
    return c;
  }

  private disabledKnapp(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const bg = this.scene.add.rectangle(0, 0, w, h, COLORS.beigeDark, 1).setOrigin(0, 0);
    const txt = this.scene.add
      .text(w / 2, h / 2, label, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#5a4a3a',
        fontStyle: '600',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);
    return c;
  }

  private utgittPille(
    x: number,
    y: number,
    w: number,
    h: number,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const bg = this.scene.add.rectangle(0, 0, w, h, COLORS.forest, 1).setOrigin(0, 0);
    const txt = this.scene.add
      .text(w / 2, h / 2, 'Utgitt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(22),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    c.add([bg, txt]);
    return c;
  }

  private byggUtgittSeksjon(
    x: number,
    y: number,
    bok: ContractedBook,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const w = ROW_WIDTH - ROW_PADDING * 2;

    const salgTall = this.scene.add
      .text(0, 0, bok.salg.toLocaleString('nb-NO'), {
        fontFamily: FONTS.display,
        fontSize: fontStr(36),
        color: '#1a1a1a',
      })
      .setOrigin(0, 0);
    c.add(salgTall);

    const salgLabel = this.scene.add
      .text(salgTall.width + 10, 18, 'solgt', {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#5a4a3a',
      })
      .setOrigin(0, 0);
    c.add(salgLabel);

    const inntektTxt = this.scene.add
      .text(0, 44, `${bok.inntekt.toLocaleString('nb-NO')} kr inntekt`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(18),
        color: '#5a4a3a',
      })
      .setOrigin(0, 0);
    c.add(inntektTxt);

    const anmeldelseRad = this.byggAnmeldelseRad(0, 78, w, bok);
    c.add(anmeldelseRad);

    const utvidet = this.utvidedeAnmeldelser.has(bok.isbn);
    if (utvidet && bok.anmeldelser.length > 0) {
      let sitatY = 130;
      for (const a of bok.anmeldelser) {
        const sitatC = this.byggSitat(0, sitatY, w, a);
        c.add(sitatC);
        sitatY += 70;
      }
    }

    return c;
  }

  private byggAnmeldelseRad(
    x: number,
    y: number,
    w: number,
    bok: ContractedBook,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);

    if (bok.anmeldelser.length === 0) {
      const ventTxt = this.scene.add
        .text(0, 8, 'Venter på anmeldelser (0/4)', {
          fontFamily: FONTS.body,
          fontSize: fontStr(18),
          color: '#6b6b6b',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      c.add(ventTxt);
      return c;
    }

    let xpos = 0;
    for (const a of bok.anmeldelser) {
      const kritiker = KRITIKERE[a.kritikerId];
      const stjerner = stjerneStreng(a.terninger);
      const t = this.scene.add
        .text(xpos, 8, `${kritiker.navn} ${stjerner}`, {
          fontFamily: FONTS.hud,
          fontSize: fontStr(16),
          color: '#1a1a1a',
        })
        .setOrigin(0, 0);
      c.add(t);
      xpos += t.width + 18;
    }

    if (bok.anmeldelser.length < 4) {
      const restTxt = this.scene.add
        .text(xpos, 8, `(${bok.anmeldelser.length}/4)`, {
          fontFamily: FONTS.body,
          fontSize: fontStr(14),
          color: '#6b6b6b',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      c.add(restTxt);
    }

    if (
      bok.authorId === SVEN_SPILLER_AUTHOR_ID &&
      typeof bok.ekstraAnmeldelsesBias === 'number' &&
      bok.ekstraAnmeldelsesBias < 0
    ) {
      const note = this.scene.add
        .text(0, 36, 'anmeldt med interesse-konflikt', {
          fontFamily: FONTS.body,
          fontSize: fontStr(12),
          color: '#a08040',
          fontStyle: 'italic',
        })
        .setOrigin(0, 0);
      c.add(note);
    }

    const utvidet = this.utvidedeAnmeldelser.has(bok.isbn);
    const togleW = 130;
    const togleX = w - togleW;
    const togleBg = this.scene.add
      .rectangle(togleX, 0, togleW, 36, COLORS.beigeDark, 1)
      .setOrigin(0, 0);
    const togleTxt = this.scene.add
      .text(togleX + togleW / 2, 18, utvidet ? 'Skjul sitater' : 'Vis sitater', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        fontStyle: '600',
      })
      .setOrigin(0.5);
    togleBg.setInteractive({ useHandCursor: true });
    togleBg.on('pointerup', () => {
      if (utvidet) this.utvidedeAnmeldelser.delete(bok.isbn);
      else this.utvidedeAnmeldelser.add(bok.isbn);
      this.render();
    });
    c.add([togleBg, togleTxt]);

    return c;
  }

  private byggSitat(
    x: number,
    y: number,
    w: number,
    anmeldelse: Anmeldelse,
  ): Phaser.GameObjects.Container {
    const c = this.scene.add.container(x, y);
    const kritiker = KRITIKERE[anmeldelse.kritikerId];

    const venstreBorder = this.scene.add
      .rectangle(0, 0, 4, 62, COLORS.ochre, 1)
      .setOrigin(0, 0);
    c.add(venstreBorder);

    const navnTxt = this.scene.add
      .text(18, 0, `${kritiker.navn} · ${stjerneStreng(anmeldelse.terninger)}`, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(14),
        color: '#5a4a3a',
        fontStyle: '600',
      })
      .setOrigin(0, 0);
    c.add(navnTxt);

    const sitatTxt = this.scene.add
      .text(18, 22, `«${anmeldelse.sitat}»`, {
        fontFamily: FONTS.body,
        fontSize: fontStr(16),
        color: '#1a1a1a',
        fontStyle: 'italic',
        wordWrap: { width: w - 36 },
      })
      .setOrigin(0, 0);
    c.add(sitatTxt);

    return c;
  }

  private erPaagaaende(s: ContractStatus): boolean {
    return s === 'redigeres' || s === 'designes' || s === 'utgis';
  }

  private statusTekst(bok: ContractedBook, dag: number): string {
    switch (bok.status) {
      case 'kontrahert':
        return 'Kontrahert';
      case 'redigeres':
        return `Redigeres ${forløpt(bok, dag)}/${FASE_DAGER.redigering} dager`;
      case 'redigert':
        return 'Redigert (klar)';
      case 'designes':
        return `Designes ${forløpt(bok, dag)}/${FASE_DAGER.design} dager`;
      case 'designet':
        return 'Designet (klar)';
      case 'utgis':
        return `Trykkes ${forløpt(bok, dag)}/${FASE_DAGER.trykk} dager`;
      case 'utgitt':
        return 'Utgitt';
    }
  }

  private stopPulses(): void {
    for (const t of this.pulseTweens) t.stop();
    this.pulseTweens = [];
  }
}

function forløpt(bok: ContractedBook, dag: number): number {
  if (bok.faseFerdigDag === null) return 0;
  const total = Math.max(1, bok.faseFerdigDag - bok.faseStartDag);
  return Math.max(0, Math.min(total, dag - bok.faseStartDag));
}

function dagerIgjen(bok: ContractedBook, dag: number): number {
  if (bok.faseFerdigDag === null) return 0;
  return Math.max(0, bok.faseFerdigDag - dag);
}
